<template>
  <Dialog
    :is-open="isOpen"
    title="Manage Overrun Reasons"
    width="600px"
    @close="$emit('close')"
  >
    <div :class="$style.overrunReasonManager">
      <div :class="$style.content">
        <div :class="$style.addSection">
          <h3>Add New Reason</h3>
          <div :class="$style.addForm">
            <input
              v-model="newReasonLabel"
              type="text"
              placeholder="Reason label (e.g., 'Technical debt')"
              :class="$style.input"
              @keyup.enter="addReason"
            />
            <input
              v-model="newReasonDescription"
              type="text"
              placeholder="Description (optional)"
              :class="$style.input"
              @keyup.enter="addReason"
            />
            <button :class="$style.addButton" @click="addReason">Add</button>
          </div>
        </div>

        <div :class="$style.reasonsSection">
          <h3>Existing Reasons</h3>
          <div :class="$style.reasonsList">
            <div
              v-for="reason in reasons"
              :key="reason.id"
              :class="$style.reasonItem"
            >
              <div :class="$style.reasonInfo">
                <div :class="$style.reasonLabel">{{ reason.label }}</div>
                <div
                  v-if="reason.description"
                  :class="$style.reasonDescription"
                >
                  {{ reason.description }}
                </div>
              </div>
              <div :class="$style.reasonActions">
                <button
                  :class="$style.editButton"
                  @click="startEdit(reason)"
                  :disabled="editingReason?.id === reason.id"
                >
                  <EditIcon :size="14" />
                </button>
                <button
                  :class="$style.deleteButton"
                  @click="deleteReason(reason.id)"
                  :disabled="reason.id === 'other'"
                >
                  <DeleteIcon :size="14" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Edit Modal -->
        <div v-if="editingReason" :class="$style.editModal" @click="cancelEdit">
          <div :class="$style.editContent" @click.stop>
            <h3>Edit Reason</h3>
            <div :class="$style.editForm">
              <input
                v-model="editLabel"
                type="text"
                placeholder="Reason label"
                :class="$style.input"
              />
              <input
                v-model="editDescription"
                type="text"
                placeholder="Description (optional)"
                :class="$style.input"
              />
              <div :class="$style.editActions">
                <button :class="$style.cancelButton" @click="cancelEdit">
                  Cancel
                </button>
                <button :class="$style.saveButton" @click="saveEdit">
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div :class="$style.footer">
        <button :class="$style.resetButton" @click="resetToDefaults">
          Reset to Defaults
        </button>
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import Dialog from "./Dialog.vue";
import { EditIcon } from "./icons";
import DeleteIcon from "./DeleteIcon.vue";
import {
  overrunReasonService,
  type OverrunReason,
} from "../services/overrunReasonService";

defineProps<{
  isOpen: boolean;
}>();

defineEmits<{
  (e: "close"): void;
}>();

const newReasonLabel = ref("");
const newReasonDescription = ref("");
const editingReason = ref<OverrunReason | null>(null);
const editLabel = ref("");
const editDescription = ref("");

const reasons = computed(() => overrunReasonService.getReasons());

function addReason() {
  const label = newReasonLabel.value.trim();
  if (!label) return;

  overrunReasonService.addReason({
    label,
    description: newReasonDescription.value.trim() || undefined,
  });

  newReasonLabel.value = "";
  newReasonDescription.value = "";
}

function startEdit(reason: OverrunReason) {
  editingReason.value = reason;
  editLabel.value = reason.label;
  editDescription.value = reason.description || "";
}

function cancelEdit() {
  editingReason.value = null;
  editLabel.value = "";
  editDescription.value = "";
}

function saveEdit() {
  if (!editingReason.value) return;

  const label = editLabel.value.trim();
  if (!label) return;

  overrunReasonService.updateReason(editingReason.value.id, {
    label,
    description: editDescription.value.trim() || undefined,
  });

  cancelEdit();
}

function deleteReason(id: string) {
  if (id === "other") return; // Prevent deleting "Other" reason

  overrunReasonService.removeReason(id);
}

function resetToDefaults() {
  if (
    confirm(
      "This will reset all overrun reasons to the default set. Are you sure?"
    )
  ) {
    overrunReasonService.resetToDefaults();
  }
}
</script>

<style module>
.overrunReasonManager {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.content {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
}

.addSection {
  margin-bottom: 30px;
}

.addSection h3 {
  margin: 0 0 15px 0;
  font-size: 1.1rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.addForm {
  display: flex;
  gap: 10px;
  align-items: flex-end;
}

.input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 14px;
}

.input:focus {
  outline: none;
  border-color: var(--primary-color);
  background: rgba(255, 255, 255, 0.15);
}

.input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.addButton {
  padding: 10px 16px;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  white-space: nowrap;
}

.addButton:hover {
  background: var(--primary-hover);
}

.reasonsSection h3 {
  margin: 0 0 15px 0;
  font-size: 1.1rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.reasonsList {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.reasonItem {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.reasonInfo {
  flex: 1;
  min-width: 0;
}

.reasonLabel {
  font-weight: 500;
  color: white;
  margin-bottom: 4px;
}

.reasonDescription {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.3;
}

.reasonActions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.editButton,
.deleteButton {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.editButton:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--primary-color);
}

.deleteButton:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--task-design);
}

.editButton:disabled,
.deleteButton:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.editModal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.editContent {
  background: #34495e;
  border-radius: 8px;
  padding: 20px;
  width: 90%;
  max-width: 400px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.editContent h3 {
  margin: 0 0 15px 0;
  font-size: 1.1rem;
  font-weight: 500;
}

.editForm {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.editActions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 10px;
}

.cancelButton,
.saveButton {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.cancelButton {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.cancelButton:hover {
  background: rgba(255, 255, 255, 0.15);
  color: white;
}

.saveButton {
  background: var(--primary-color);
  color: white;
}

.saveButton:hover {
  background: var(--primary-hover);
}

.footer {
  padding: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: center;
}

.resetButton {
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.resetButton:hover {
  background: rgba(255, 255, 255, 0.15);
  color: white;
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .header {
    padding: 15px;
  }

  .content {
    padding: 15px;
  }

  .addForm {
    flex-direction: column;
    align-items: stretch;
  }

  .editActions {
    flex-direction: column;
  }
}
</style>
